import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AllNewJobsListAction } from "../../../Redux/action/AllNewJobsListAction";
import Pagination from "../../Common/Pagination/Pagination";
import BackButton from "../../Common/BackButton/BackButton";
import DOMPurify from "dompurify";

import {
  Box,
  TextField,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Divider,
  Paper,
  Skeleton,
  InputAdornment,
  Stack,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Button,
  Autocomplete,
} from "@mui/material";
import {
  Search,
  Tune,
  FmdGood,
  BusinessCenter,
  History,
  Code,
  Badge,
  CalendarMonth,
  ChevronRight,
  SearchOff,
} from "@mui/icons-material";
import { fetchSkillDetails } from "../../../Redux/action/MasterSkillAction";

const THEME_BLUE = "#2d6d96";
const TEXT_DARK = "#0f172a";

const AllNewJobsList = () => {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [pageNumber, setPageNumber] = useState(0);

  const [filters, setFilters] = useState({
    workType: { REMOTE: false, ONSITE: false, HYBRID: false },
    jobtype: {
      PERMANENT: false,
      CONTRACT: false,
      INTERNSHIP: false,
      FREELANCE: false,
    },
    selectedSkills: [],
  });

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userResult } = useSelector((state) => state.Auth);
  const vendorId = userResult?.vendorid;

  const { result = {}, loading } = useSelector(
    (state) => state?.AllNewJobsListData,
  );
  const userInfo = result?.content || [];
  const displayCount = result?.totalElements || 0;
  const totalPages = result?.totalPages || 0;
  const skillList =
    useSelector((state) => state?.MasterTableDetails?.skill?.content) || [];
   
  const skillOptions = useMemo(() => {
    if (!Array.isArray(skillList)) return [];

    const uniqueSkills = new Set();

    skillList.forEach((s) => {
      if (s?.skillName) {
        uniqueSkills.add(s.skillName);
      }
    });

    return Array.from(uniqueSkills).map((skill) => ({
      value: skill,
      label: skill,
    }));
  }, [skillList]);

  const handleFilterChange = (category, name) => (event) => {
    setFilters((prev) => ({
      ...prev,
      [category]: { ...prev[category], [name]: event.target.checked },
    }));
    setPageNumber(0);
  };

  const handleSkillsChange = (event, newValue) => {
    event.preventDefault();

    setFilters((prev) => ({
      ...prev,
      selectedSkills: Array.isArray(newValue)
        ? newValue.map((s) => s.value)
        : [],
    }));
    setPageNumber(0);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPageNumber(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  const formatExperience = (minExp, maxExp) => {
    const min = Number(String(minExp ?? "").replace(/\D/g, ""));
    const max = Number(String(maxExp ?? "").replace(/\D/g, ""));

    if (isNaN(min) && isNaN(max)) return "N/A";

    if (min === 0 && max === 0) return "Fresher";

    if (min === 0 && max > 0) return `0 – ${max} years`;

    if (!isNaN(min) && isNaN(max)) return `${min}+ years`;

    if (min === max) return `${min} years`;

    return `${min} – ${max} years`;
  };

  const fetchJobs = useCallback(() => {
    if (!vendorId) return;
    const getActiveKeys = (obj) =>
      Object.keys(obj)
        .filter((k) => obj[k])
        .join(",");

    dispatch(
      AllNewJobsListAction({
        vendorId,
        pageNumber,
        pageSize: 10,
        search: debouncedSearch,
        workType: getActiveKeys(filters.workType),
        jobType: getActiveKeys(filters.jobtype),
        skills: filters.selectedSkills,
      }),
    );
  }, [vendorId, pageNumber, debouncedSearch, filters, dispatch]);

  useEffect(() => {
    fetchJobs();
    dispatch(fetchSkillDetails());
  }, [fetchJobs]);

  const handlePageChange = (page) => {
    setPageNumber(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const SkeletonCard = () => (
    <Card sx={{ mb: 2, borderRadius: "10px" }}>
      <CardContent>
        <Skeleton width="60%" height={25} />
        <Skeleton width="40%" height={20} sx={{ mt: 1 }} />
        <Skeleton width="100%" height={15} sx={{ mt: 2 }} />
        <Skeleton width="85%" height={15} />
        <Skeleton width="70%" height={15} />
      </CardContent>
    </Card>
  );
  return (
    <Box
      sx={{
        bgcolor: "#f1f5f9",
        minHeight: "100vh",
        py: 4,
        px: { xs: 2, md: 6 },
      }}
    >
      <Grid container spacing={3}>
        {/* --- LEFT SIDE FILTER PANEL --- */}
        <Grid item xs={12} md={4} lg={3}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: "16px",
              border: "1px solid #cbd5e1",
              position: "sticky",
              top: 24,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mb: 3 }}
            >
              <Tune sx={{ color: THEME_BLUE }} />
              <Typography
                variant="h6"
                sx={{ fontWeight: 800, color: TEXT_DARK }}
              >
                Filters
              </Typography>
            </Stack>

            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, color: TEXT_DARK, mb: 1 }}
              >
                Search Role
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Keywords..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" sx={{ color: THEME_BLUE }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    "& fieldset": {
                      borderColor: "#2d6d96",
                    },
                    "&:hover fieldset": {
                      borderColor: "#245a7c", // slightly darker
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#2d6d96",
                      borderWidth: "1.5px",
                    },
                  },
                }}
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, color: TEXT_DARK, mb: 1 }}
              >
                Skills
              </Typography>
              <Autocomplete
                multiple
                options={skillOptions}
                value={filters.selectedSkills}
                onChange={handleSkillsChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Select..."
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        "& fieldset": {
                          borderColor: "#2d6d96",
                        },
                        "&:hover fieldset": {
                          borderColor: "#245a7c", // slightly darker
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#2d6d96",
                          borderWidth: "1.5px",
                        },
                      },
                    }}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option}
                      size="small"
                      {...getTagProps({ index })}
                      sx={{
                        bgcolor: "#e0e7ff",
                        color: THEME_BLUE,
                        fontWeight: 700,
                      }}
                    />
                  ))
                }
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, color: TEXT_DARK, mt: 1 }}
            >
              Work Model
            </Typography>
            <FormGroup>
              {["REMOTE", "ONSITE", "HYBRID"].map((type) => (
                <FormControlLabel
                  key={type}
                  control={
                    <Checkbox
                      size="small"
                      checked={filters.workType[type]}
                      onChange={handleFilterChange("workType", type)}
                      sx={{
                        color: "#2d6d96",
                        "&.Mui-checked": {
                          color: "#2d6d96",
                        },
                      }}
                    />
                  }
                  label={
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color="#475569"
                    >
                      {type}
                    </Typography>
                  }
                />
              ))}
            </FormGroup>

            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, color: TEXT_DARK, mt: 2 }}
            >
              Job Type
            </Typography>
            <FormGroup>
              {["PERMANENT", "CONTRACT", "INTERNSHIP", "FREELANCE"].map(
                (type) => (
                  <FormControlLabel
                    key={type}
                    control={
                      <Checkbox
                        size="small"
                        checked={filters.jobtype[type]}
                        onChange={handleFilterChange("jobtype", type)}
                        sx={{
                          color: "#2d6d96",
                          "&.Mui-checked": {
                            color: "#2d6d96",
                          },
                        }}
                      />
                    }
                    label={
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color="#475569"
                      >
                        {type}
                      </Typography>
                    }
                  />
                ),
              )}
            </FormGroup>

            <Button
              fullWidth
              variant="contained"
              size="small"
              sx={{
                mt: 3,
                textTransform: "none",
                borderRadius: "10px",
                bgcolor: THEME_BLUE,
                boxShadow: "none",
              }}
              onClick={() =>
                setFilters({
                  workType: { REMOTE: false, ONSITE: false, HYBRID: false },
                  jobtype: {
                    PERMANENT: false,
                    CONTRACT: false,
                    INTERNSHIP: false,
                    FREELANCE: false,
                  },
                  selectedSkills: [],
                })
              }
            >
              Clear All
            </Button>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8} lg={9}>
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{ mb: 3 }}
          >
            <BackButton />

            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                color: TEXT_DARK,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              Find Your Next Opportunity
              <Typography
                component="span"
                sx={{
                  fontWeight: 600,
                  fontSize: "0.95rem",
                  color: "text.secondary",
                }}
              >
                ({displayCount || 0})
              </Typography>
            </Typography>
          </Stack>

          {loading ? (
            [1, 2, 3].map((_, index) => <SkeletonCard key={index} />)
          ) : userInfo.length > 0 ? (
            userInfo.map((item) => (
              <Card
                key={item.jobId}
                elevation={0}
                sx={{
                  mb: 2,
                  borderRadius: "16px",
                  border: "1px solid #cbd5e1",
                  transition: "0.2s",
                  "&:hover": {
                    borderColor: THEME_BLUE,
                    boxShadow: "0 4px 15px rgba(0,0,0,0.06)",
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  {/* Header Row */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 800, color: TEXT_DARK, flex: 1, pr: 2 }}
                    >
                      {item.jobTitle}
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      sx={{
                        color: "#2d6d96",
                        bgcolor: "#f8fafc",
                        px: 1.2,
                        py: 0.6,
                        borderRadius: "8px",
                        border: "1px solid #f1f5f9",
                      }}
                    >
                      <CalendarMonth sx={{ fontSize: 16 }} />
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 800, fontSize: "11px" }}
                      >
                        Posted on : {item?.postedDate?.split(" ")[0] || "N/A"}
                      </Typography>
                    </Stack>
                  </Box>

                  {/* ID & Details Row */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: THEME_BLUE,
                        fontWeight: 900,
                        bgcolor: "#f0f7ff",
                        px: 1.5,
                        py: 0.5,
                        borderRadius: "6px",
                        border: `1px solid ${THEME_BLUE}20`,
                        letterSpacing: "0.5px",
                      }}
                    >
                      JOB ID: {item.jobUuid}
                    </Typography>
                    <Button
                      variant="text"
                      size="small"
                      endIcon={<ChevronRight sx={{ fontSize: 20 }} />}
                      onClick={() => navigate(`/view_job/${item.jobId}`)}
                      sx={{
                        color: THEME_BLUE,
                        textTransform: "none",
                        fontWeight: 800,
                        p: 0,
                        "&:hover": {
                          bgcolor: "transparent",
                          textDecoration: "underline",
                        },
                      }}
                    >
                      View Full Details
                    </Button>
                  </Box>

                  <Typography
                    variant="body2"
                    sx={{
                      color: "#475569",
                      mb: 2.5,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      lineHeight: 1.7,
                      fontSize: "14px",
                    }}
                  >
                    <span
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(item.jobDescription || ""),
                      }}
                    />
                  </Typography>

                  <Grid container spacing={1.5}>
                    {[
                      {
                        icon: <FmdGood />,
                        label: "LOCATION",
                        value:
                          item?.workType === "REMOTE"
                            ? "Remote"
                            : item?.workType === "HYBRID" ||
                                item?.workType === "ONSITE"
                              ? [item?.city, item?.state, item?.country]
                                  .filter(Boolean)
                                  .join(", ") || "Not Disclosed"
                              : "Not Disclosed",
                      },
                      {
                        icon: <BusinessCenter />,
                        label: "JOB TYPE",
                        value:
                          item?.jobType === "PERMANENT"
                            ? "Permanent"
                            : item?.jobType === "CONTRACT"
                              ? "Contract"
                              : item?.jobType === "INTERNSHIP"
                                ? "Internship"
                                : item?.jobType === "FREELANCE"
                                  ? "Freelance"
                                  : "Not Disclosed",
                      },
                      {
                        icon: <Badge />,
                        label: "EMPLOYMENT TYPE",
                        value:
                          item?.employmentType === "FULL_TIME"
                            ? "Full Time"
                            : item?.employmentType === "PART_TIME"
                              ? "Part Time"
                              : item?.employmentType === "FLEXIBLE"
                                ? "Flexible"
                                : "N/A",
                      },
                      {
                        icon: <History />,
                        label: "EXPERIENCE",
                        value: formatExperience(
                          item?.minimumExperience,
                          item?.maximumExperience,
                        ),
                      },
                    ].map((info) => (
                      <Grid item xs={12} sm={3} key={info.label}>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{
                            bgcolor: "#f8fafc",
                            p: 1.2,
                            borderRadius: "10px",
                            border: "1px solid #f1f5f9",
                          }}
                        >
                          {React.cloneElement(info.icon, {
                            sx: { color: THEME_BLUE, fontSize: 18 },
                          })}
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              variant="caption"
                              sx={{
                                color: THEME_BLUE,
                                fontWeight: 800,
                                display: "block",
                                fontSize: "8.5px",
                                letterSpacing: "0.5px",
                              }}
                            >
                              {info.label}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 800,
                                color: TEXT_DARK,
                                fontSize: "11.5px",
                              }}
                              noWrap
                            >
                              {info.value}
                            </Typography>
                          </Box>
                        </Stack>
                      </Grid>
                    ))}
                  </Grid>

                  {item.requiredSkills?.length > 0 && (
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      sx={{ mt: 3 }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: THEME_BLUE,
                          p: 0.8,
                          borderRadius: "8px",
                        }}
                      >
                        <Code sx={{ fontSize: 18, color: "#fff" }} />
                      </Box>
                      <Stack
                        direction="row"
                        spacing={1.2}
                        sx={{ flexWrap: "wrap" }}
                      >
                        {item.requiredSkills.slice(0, 3).map((skill, index) => (
                          <Chip
                            key={index}
                            label={skill}
                            sx={{
                              fontWeight: 700,
                              fontSize: "12px",
                              height: 32,
                              px: 1,
                              bgcolor: "#ffffff",
                              border: "2px solid #e2e8f0",
                              color: TEXT_DARK,
                              "&:hover": {
                                bgcolor: "#f8fafc",
                                borderColor: THEME_BLUE,
                              },
                            }}
                          />
                        ))}
                        {item.requiredSkills.length > 3 && (
                          <Typography
                            variant="caption"
                            sx={{
                              alignSelf: "center",
                              color: "#2d6d96",
                              fontWeight: 800,
                              fontSize: "12px",
                              ml: 1,
                            }}
                          >
                            +{item.requiredSkills.length - 3} More
                          </Typography>
                        )}
                      </Stack>
                    </Stack>
                  )}
                </CardContent>
              </Card>
            ))
          ) : (
            <>
              <Box
                sx={{
                  textAlign: "center",
                  py: 15,
                  bgcolor: "#fff",
                  borderRadius: "16px",
                  border: "1px solid #cbd5e1",
                }}
              >
                <SearchOff sx={{ fontSize: 60, color: "#cbd5e1", mb: 2 }} />
                <Typography
                  variant="h5"
                  sx={{ color: "#94a3b8", fontWeight: 600 }}
                >
                  No jobs match your search 🔍
                </Typography>
                <Typography variant="body2" sx={{ color: "#94a3b8", mt: 1 }}>
                  Try adjusting your filters or search keywords to find more
                  opportunities.
                </Typography>
              </Box>
            </>
          )}

          {/* PAGINATION */}
          {!loading && totalPages > 0 && (
            <Box sx={{ mt: 4, display: "flex", justifyContent: "center" }}>
              <Pagination
                totalPages={totalPages}
                currentPage={pageNumber}
                onPageChange={handlePageChange}
              />
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default AllNewJobsList;
